import * as React from 'react'
import styled, { css } from 'styled-components'

import { t, tn } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import * as plume from '@ulule/owl-kit-components/next'

import { useDisplayMode } from '../hooks/useDisplayMode'
import { ConfettiBanner } from '../symbols'

import * as UFE from '../UFE'

type HeaderProps = {
  avatar?: string
  backgroundImage?: string
  bio?: string
  name: string
  project: models.project.ProjectForOwner
  setShareModalOpen: (open: boolean, params?: any) => void
}

export function Header({
  avatar,
  backgroundImage,
  bio,
  name,
  project,
  setShareModalOpen,
}: HeaderProps): React.ReactElement {
  const displayMode = useDisplayMode(project)
  const isEditMode = displayMode === 'edit'

  return (
    <HeaderLayout>
      <HeaderBackdrop
        backgroundImageDesktop={backgroundImage}
        backgroundImageMobile={avatar}
        onClick={() => {
          if (isEditMode && plume.utils.isMobile() && typeof window !== 'undefined') {
            window.location.href = `/projects/${project.id}/backoffice/#avatar`
          }
        }}
      >
        {!avatar && <plume.glyphs.crowdfunding.Owl size={280} />}
        {isEditMode && plume.utils.isMobile() ? (
          <></>
        ) : backgroundImage && isEditMode ? (
          <a href={`/projects/${project.id}/backoffice/#image`}>
            <plume.EditButton glyph={<plume.glyphs.stroke.Edit size={18} />}>{t('Edit')}</plume.EditButton>
          </a>
        ) : (
          isEditMode && (
            <a href={`/projects/${project.id}/backoffice/#image`}>
              <plume.EditButton glyph={<plume.glyphs.stroke.Plus size={18} />}>{t('Add an image')}</plume.EditButton>
            </a>
          )
        )}
        {avatar ? (
          <>
            {isEditMode && (
              <AvatarUploadOverlay as="a" href={`/projects/${project.id}/backoffice/#avatar`}>
                <plume.glyphs.stroke.Camera size={54} />
                <plume.styles.copy.S>{t('Modify this profile image')}</plume.styles.copy.S>
              </AvatarUploadOverlay>
            )}
            <Avatar src={avatar} type="extra-large" />
          </>
        ) : (
          <>
            <AvatarPlaceholder
              $src={plume.utils.svg.toDataUrl(plume.glyphs.crowdfunding.Owl, {
                color: plume.COLORS.PRIMARY_BLUE_700,
              })}
              type="extra-large"
            />
          </>
        )}
      </HeaderBackdrop>
      <HeaderOptions>
        <plume.Menu title={<plume.glyphs.stroke.Ellipsis size={24} />} elementType="div" align="right" dropdownOnClick>
          <li>
            <plume.styles.copy.S>
              <plume.Link
                disabled={!models.project.isOnline(project)}
                href={`/discussions/compose/${project.owner && project.owner.id}`}
              >
                {t('Send a message')}
              </plume.Link>
            </plume.styles.copy.S>
          </li>
          <li>
            <plume.styles.copy.S>
              <plume.LinkAsButton disabled={!models.project.isOnline(project)} onClick={() => setShareModalOpen(true)}>
                {t('Share this page')}
              </plume.LinkAsButton>
            </plume.styles.copy.S>
          </li>
        </plume.Menu>
      </HeaderOptions>
      <HeaderContent>
        <HeaderTitle>
          <plume.styles.heading.L asDesktop="XL">
            {name}{' '}
            {isEditMode && (
              <a href={`/projects/${project.id}/backoffice/#name`}>
                <plume.EditButton glyph={<plume.glyphs.stroke.Edit size={18} />}>{t('Edit')}</plume.EditButton>
              </a>
            )}
          </plume.styles.heading.L>
        </HeaderTitle>
        {bio ? (
          <HeaderBio>
            <plume.styles.copy.S
              as="span"
              asDesktop="L"
              dangerouslySetInnerHTML={{
                __html: bio,
              }}
            />
          </HeaderBio>
        ) : (
          isEditMode && (
            <HeaderBio>
              <plume.styles.copy.S as="span" asDesktop="L">
                {t('Describe what you do in a few words')}
              </plume.styles.copy.S>
            </HeaderBio>
          )
        )}
        <HeaderStats>
          {project.subscriptions_count !== undefined && (
            <plume.styles.copy.S asDesktop="L">
              {`${plume.accounting.formatNumber(project.subscriptions_count)}\u00A0`}
              <span>{tn('member', 'members', project.subscriptions_count)}</span>
            </plume.styles.copy.S>
          )}
          {project.show_subscriptions_amount && project.subscriptions_amount !== undefined && (
            <plume.styles.copy.S asDesktop="L">
              {`${t('%(currency)s%(amount)s', {
                amount: plume.accounting.formatNumber(project.subscriptions_amount),
                currency: UFE.CURRENCIES.SYMBOLS[project.currency].symbol,
              })}\u00A0`}
              <span>{t('/ month')}</span>
            </plume.styles.copy.S>
          )}
          <plume.styles.copy.S asDesktop="L">
            {`${plume.accounting.formatNumber(project.news_count)}\u00A0`}
            <span>{tn('post', 'posts', project.news_count)}</span>
          </plume.styles.copy.S>
        </HeaderStats>
      </HeaderContent>
    </HeaderLayout>
  )
}

const Avatar = styled(plume.styles.image.Avatar)`
  z-index: 1;

  &:hover {
    z-index: 0;
  }
`

const AvatarPlaceholder = styled(plume.styles.image.Avatar)<{ $src: string }>`
  background-color: ${({ theme }) => `${theme?.colors?.background ?? plume.COLORS.PRIMARY_BLUE_100}`};
  background-image: ${({ $src }) => `url(${$src})`};
  background-repeat: no-repeat;
  background-size: 120px;
`

const AvatarUploadOverlay = styled.div`
  align-items: center;
  background-color: ${plume.COLORS.hexToRgb(plume.COLORS.SECONDARY_YELLOW_200, 0.85)};
  box-sizing: border-box;
  color: ${plume.COLORS.SECONDARY_YELLOW_500};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  height: 100%;
  justify-content: center;
  object-fit: cover;
  position: absolute;
  text-decoration: none;
  width: 100%;
  z-index: -1;

  &:hover {
    & + ${Avatar} {
      z-index: 0;
    }
  }

  ${plume.styles.copy.S} {
    font-weight: 700; // HACK: Irregular font manipulation
    text-align: center;
    width: 106px;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    border-radius: 50%;
    border: 4px solid ${plume.COLORS.PRIMARY_GREY_000};
    height: 180px;
    left: 50%;
    top: 171px;
    transform: translate(-50%, 0%);
    width: 180px;
    z-index: 1;
  }
`

const HeaderLayout = styled.header`
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  justify-items: center;
  align-items: center;
  position: relative;
`

const HeaderBackdrop = styled.div<{
  avatar?: string
  backgroundImageDesktop?: string
  backgroundImageMobile?: string
}>`
  align-items: center;
  background-color: ${({ theme }) => `${theme?.colors?.background ?? plume.COLORS.PRIMARY_BLUE_100}`};
  background-position: center;
  background-size: cover;
  color: ${({ theme }) => `${theme?.colors?.hover ?? plume.COLORS.PRIMARY_BLUE_700}`};
  display: flex;
  height: 100vw;
  justify-content: center;
  position: relative;
  width: 100%;

  ${plume.glyphs.crowdfunding.Owl} {
    margin-top: 32px;
  }

  &:active {
    z-index: 0;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-bottom: 72px;

    ${plume.glyphs.crowdfunding.Owl} {
      display: none;
    }
  }

  ${({ backgroundImageDesktop, backgroundImageMobile, avatar, theme }) => {
    if (avatar || backgroundImageDesktop) {
      return css`
        background-image: url(${backgroundImageMobile});
        background-position: center;
        background-size: cover;

        @media screen and ${plume.BREAKPOINTS.TABLET} {
          background-image: url(${backgroundImageDesktop});
          height: 297px;
        }
      `
    } else if (!backgroundImageDesktop) {
      return css`
        background-image: url(${backgroundImageMobile});
        height: 100vw;
        width: 100%;
        @media screen and ${plume.BREAKPOINTS.TABLET} {
          background-image: url(${plume.utils.svg.toDataUrl(ConfettiBanner, { theme: theme })});
          height: 297px;
        }
      `
    }
  }};

  ${plume.styles.image.Avatar} {
    display: none;
    border-width: 4px;
    position: absolute;
    top: 171px;
    left: 50%;
    transform: translate(-50%, 0%);

    @media screen and ${plume.BREAKPOINTS.TABLET} {
      display: block;
    }
  }

  ${plume.EditButton} {
    display: none;
    position: absolute;
    top: 4%;
    right: 4%;

    @media screen and ${plume.BREAKPOINTS.TABLET} {
      display: revert;
      right: 16px;
    }
  }
`

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-inline: 16px;
  text-align: center;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-top: -24px;
  }
`

const HeaderOptions = styled.div`
  justify-self: flex-end;
  position: relative;
  top: 6px;
  right: 16px;
  z-index: 10;

  ${plume.glyphs.stroke.Ellipsis} {
    color: ${plume.COLORS.PRIMARY_SAND_500};
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    top: -60px;
    right: calc(8% + 120px);
  }
  @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
    right: 25%;
  }

  ${plume.LinkAsButton} {
    padding: 0;
  }
`

const HeaderStats = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  row-gap: 12px;
  flex-wrap: wrap;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    justify-content: space-evenly;
  }

  ${plume.styles.copy.Base} {
    color: ${plume.COLORS.PRIMARY_GREY_900};
    font-weight: 600; // HACK: Irregular font manipulation
    white-space: nowrap;
    padding: 0;

    span {
      color: ${plume.COLORS.PRIMARY_SAND_700};
      font-weight: 400; // HACK: Irregular font manipulation
    }
  }
`

const HeaderTitle = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const HeaderBio = styled(plume.WysiwygContent)`
  max-width: 288px;
  padding: 10px 0 18px;

  ${plume.styles.copy.S} > p,
  ${plume.styles.copy.S} strong {
    font-size: inherit;

    * {
      margin: 0;
    }
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    max-width: 464px;
    padding: 16px 0;
  }
`
